import React, { useCallback } from 'react';
import { find } from 'lodash-es';
import querystring from 'query-string';
import {
  HandleLink,
  GNB_REMODELING_EVENT,
  GNB_REMODELING_GOODS,
  GNB_REMODELING_REHOUSE,
  GNB_REMODELING_SECTION,
  GNB_REMODELING_SHOP,
  GNB_REMODELING_START,
  TYPE_VALUE_MOBILE,
  VIEW_TYPE_REMODELING,
  VIEW_TYPE_HOMEIDEA,
  VIEW_TYPE_MALL,
  DisplayMallGnbV5,
  DisplayMallSnbV5,
  NewDisplayHandleLink,
  NewDisplayHandleAction,
} from '@display-component/shared';
import { useGnb } from '@display-component/mobile';
import TrackingService from '@/services/tracking';
import { useWebApp } from '@/providers/WebAppProvider';
import { useRouter } from 'next/router';
import { useStore } from 'react-redux';
import { authGateway } from '@/di-container/auth/gateway/client';
import { NewTextButton, useToast } from '@design-system/mobile';
import { useAlert } from '@/providers/AlertProvider';
import { useConfirm } from '@/providers/ConfirmProvider';
import { TOAST_Z_INDEX } from '@/styles';

type OneLinkType = 'gdbLink' | 'gnb' | 'home';

export type HandleOpenAppArgs = {
  target?: string;
  channel?: string;
  module?: string;
  value?: string;
};

const useDisplayComponent = () => {
  const router = useRouter();

  const {
    isApp,
    isWeb,
    handleOpenWebPopUp,
    handleOpenApp: openApp,
    handleNewOpenApp: newOpenApp,
  } = useWebApp();

  const store = useStore();
  const { loginGateway } = authGateway(store.dispatch);
  const { toast } = useToast();
  const { handleOpen: handleAlertOpen } = useAlert();
  const { handleOpen: handleConfirmOpen } = useConfirm();

  const handleOpenUrl = useCallback(
    (url = '', wiseLogParam?: string) => {
      const targetUrl = TrackingService.combineWiseLogParam(
        url.startsWith('http')
          ? url
          : `${process.env.NEXT_PUBLIC_MWEB_HOST}${
              url.startsWith('/') ? '' : '/'
            }${url}`,
        wiseLogParam,
      );

      if (isApp()) {
        handleOpenWebPopUp({ url: targetUrl });
      } else {
        if (typeof window !== 'undefined') {
          window.location.href = targetUrl;
        } else {
          router.push(targetUrl);
        }
      }
    },
    [handleOpenWebPopUp, isApp, router],
  );

  const { gnbList: remodelingGnbMenuList } = useGnb({
    viewType: VIEW_TYPE_REMODELING,
    typeValue: TYPE_VALUE_MOBILE,
  });
  const handleAppLinkGnbRemodeling = useCallback<HandleLink>(
    (appLink) => {
      const found = find(remodelingGnbMenuList, {
        dispTemplateNo: appLink?.appLinkVal,
      });

      if (found) {
        const { module: gnbModule } = found as DisplayMallGnbV5;
        switch (gnbModule) {
          case GNB_REMODELING_SECTION:
            handleOpenUrl('/main', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_GOODS:
            handleOpenUrl('/goods', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_SHOP:
            handleOpenUrl(
              `/shop/search${
                appLink?.appLinkAddQuery ? `?${appLink?.appLinkAddQuery}` : ''
              }`,
              appLink?.wiseLogParam,
            );
            break;
          case GNB_REMODELING_START:
            handleOpenUrl('/remodeling-start', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_EVENT:
            handleOpenUrl('/event', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_REHOUSE:
            handleOpenUrl('/rehouse', appLink?.wiseLogParam);
            break;
          default:
            handleOpenUrl(`/main?module=${gnbModule}`, appLink?.wiseLogParam);
            break;
        }
      } else {
        handleOpenUrl(`/main`, appLink?.wiseLogParam);
      }
    },
    [handleOpenUrl, remodelingGnbMenuList],
  );

  const handleAppLinkCateRemodeling = useCallback<HandleLink>(
    (appLink) => {
      const query = querystring.stringify({
        ctgNo: appLink?.appLinkVal,
      });
      handleOpenUrl(`/goods?${query}`, appLink?.wiseLogParam);
    },
    [handleOpenUrl],
  );

  const { gnbList: mallGnbMenuList } = useGnb({
    viewType: VIEW_TYPE_MALL,
    typeValue: TYPE_VALUE_MOBILE,
  });

  const handleAppLinkGnbMall = useCallback<HandleLink>(
    (appLink) => {
      const found = find(mallGnbMenuList, {
        dispTemplateNo: appLink?.appLinkVal,
      });

      if (found) {
        const subFound = find((found as DisplayMallGnbV5)?.snbList, {
          dispTemplateNo: appLink?.appLinkSubVal,
        }) as DisplayMallSnbV5;

        const { module: gnbModule } = found as DisplayMallGnbV5;
        const subQueryParam = subFound?.snbCd ? `&snb=${subFound.snbCd}` : '';

        switch (gnbModule) {
          default:
            handleOpenUrl(
              `${process.env.NEXT_PUBLIC_STORE_MWEB_HOST}/furnishing?module=${gnbModule}${subQueryParam}`,
              appLink?.wiseLogParam,
            );
            break;
        }
      } else {
        handleOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/mall.html`,
          appLink?.wiseLogParam,
        );
      }
    },
    [handleOpenUrl, mallGnbMenuList],
  );

  const handleAppLinkCateMall = useCallback<HandleLink>(
    (appLink) => {
      if (appLink && appLink.appLinkOption) {
        handleOpenUrl(
          `${process.env.NEXT_PUBLIC_STORE_MWEB_HOST}/category/${appLink.appLinkOption.ctgNo}`,
          appLink?.wiseLogParam,
        );
      }
    },
    [handleOpenUrl],
  );

  const { gnbList: homeIdeaGnbMenuList } = useGnb({
    viewType: VIEW_TYPE_HOMEIDEA,
    typeValue: TYPE_VALUE_MOBILE,
  });
  const handleAppLinkGnbHomeIdea = useCallback<HandleLink>(
    (appLink) => {
      const found = find(homeIdeaGnbMenuList, {
        dispTemplateNo: appLink?.appLinkVal,
      });
      if (found) {
        const { module: gnbModule } = found as DisplayMallGnbV5;
        switch (gnbModule) {
          default:
            handleOpenUrl(
              `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/interior.html?module=${gnbModule}`,
              appLink?.wiseLogParam,
            );
            break;
        }
      } else {
        handleOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/interior.html`,
          appLink?.wiseLogParam,
        );
      }
    },
    [handleOpenUrl, homeIdeaGnbMenuList],
  );

  const handleLink = useCallback<HandleLink>(
    (appLink) => {
      if (appLink?.appLinkTarget === 'LINK') {
        if (appLink?.appLinkVal) {
          handleOpenUrl(appLink.appLinkVal, appLink?.wiseLogParam);
        }
      }
      if (appLink?.appLinkTarget === 'GNB') {
        switch (appLink?.appLinkChannel) {
          case 'MALL':
            if (isWeb()) {
              handleAppLinkGnbMall(appLink);
            } else {
              openApp(appLink);
            }
            break;
          case 'HOMEIDEA':
            if (isWeb()) {
              handleAppLinkGnbHomeIdea(appLink);
            } else {
              openApp(appLink);
            }
            break;
          default:
            if (isWeb()) {
              handleAppLinkGnbRemodeling(appLink);
            } else {
              openApp(appLink);
            }
            break;
        }
      }
      if (appLink?.appLinkTarget === 'CATE') {
        switch (appLink?.appLinkChannel) {
          case 'MALL':
            if (isWeb()) {
              handleAppLinkCateMall(appLink);
            } else {
              openApp(appLink);
            }
            break;
          default:
            if (isWeb()) {
              handleAppLinkCateRemodeling(appLink);
            } else {
              openApp(appLink);
            }
            break;
        }
      }
      if (appLink?.appLinkTarget === 'PLAN') {
        if (appLink?.appLinkVal) {
          handleOpenUrl(
            `/plan/${appLink.appLinkVal}.html?shpNo=${appLink.appLinkVal}`,
            appLink?.wiseLogParam,
          );
        }
      }
    },
    [
      handleAppLinkCateMall,
      handleAppLinkCateRemodeling,
      handleAppLinkGnbHomeIdea,
      handleAppLinkGnbMall,
      handleAppLinkGnbRemodeling,
      handleOpenUrl,
      isWeb,
      openApp,
    ],
  );

  const handleOpenApp = useCallback(
    ({
      target = 'GNB',
      channel = 'REMODELING',
      module = GNB_REMODELING_SECTION,
      value,
    }: HandleOpenAppArgs) => {
      if (target === 'GNB') {
        if (channel === 'REMODELING') {
          const found = find(remodelingGnbMenuList, { module });
          if (found) {
            openApp({
              appLinkTarget: target,
              appLinkChannel: channel,
              appLinkVal: found.dispTemplateNo,
            });
          }
        }
        if (channel === 'HOMEIDEA') {
          const found = find(homeIdeaGnbMenuList, { module });
          if (found) {
            openApp({
              appLinkTarget: target,
              appLinkChannel: channel,
              appLinkVal: found.dispTemplateNo,
            });
          }
        }
      } else {
        openApp({
          appLinkTarget: target,
          appLinkChannel: channel,
          appLinkVal: value || '',
        });
      }
    },
    [homeIdeaGnbMenuList, openApp, remodelingGnbMenuList],
  );

  const handleOneLink = (type: OneLinkType, gdsNo?: number) => {
    // main, gdsLink, gnb, plan
    if (type === 'gdbLink') {
      window.location.href = `https://hanssem.onelink.me/tm0Q?pid=hanssem_test&c=test_c&af_adset=test_adset&af_ad=test_ad&af_channel=test_chnnel&af_dp=hss://gdslink?deep_link_sub1=${gdsNo}&af_force_deeplink=true&deep_link_value=gdslink&deep_link_sub1=${gdsNo}`;
    } else if (type === 'gnb') {
      window.location.href =
        'https://hanssem.onelink.me/tm0Q?pid=hanssem&af_dp=hss://applink?deep_link_sub1=MALL&deep_link_sub2=GNB&deep_link_sub3=home&af_force_deeplink=true&deep_link_value=applink&deep_link_sub1=MALL&deep_link_sub2=GNB&deep_link_sub3=home';
    } else if (type === 'home') {
      window.location.href =
        'https://hanssem.onelink.me/tm0Q?pid=hanssem&af_dp=hss://applink';
    }
  };

  const handleNewAppLinkGnbHomeIdea = useCallback<NewDisplayHandleLink>(
    (appLink) => {
      if (appLink?.applink?.linkUrl) {
        handleOpenUrl(appLink.applink.linkUrl);
      } else {
        handleOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/interior.html`,
        );
      }
    },
    [handleOpenUrl],
  );

  const handleNewAppLinkGnbRemodeling = useCallback<NewDisplayHandleLink>(
    (appLink) => {
      if (appLink?.applink?.linkUrl) {
        handleOpenUrl(appLink.applink.linkUrl);
      } else {
        handleOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/interior.html`,
        );
      }
    },
    [handleOpenUrl],
  );

  const handleNewAppLinkGnbMain = useCallback<NewDisplayHandleLink>(() => {
    handleOpenUrl(`${process.env.NEXT_PUBLIC_STORE_MWEB_HOST}`);
  }, [handleOpenUrl]);

  const handleNewAppLinkGnbMall = useCallback<NewDisplayHandleLink>(
    (appLink) => {
      if (appLink?.applink?.linkUrl) {
        handleOpenUrl(appLink.applink.linkUrl);
      } else {
        handleOpenUrl(`${process.env.NEXT_PUBLIC_STORE_MWEB_HOST}/furnishing`);
      }
    },
    [handleOpenUrl],
  );

  const handleNewAppLinkCate = useCallback<NewDisplayHandleLink>(
    (appLink) => {
      if (appLink?.applink?.linkUrl) {
        handleOpenUrl(appLink.applink.linkUrl);
      }
    },
    [handleOpenUrl],
  );

  const handleNewLink = useCallback<NewDisplayHandleLink>(
    (appLink) => {
      console.log('[TEST-DISPLAY - appLink]', appLink);

      if (appLink?.applink?.appLinkTarget === 'URL') {
        handleOpenUrl(appLink.applink.linkUrl);
      }

      if (appLink?.applink?.appLinkTarget === 'GNB') {
        switch (appLink?.applink?.appLinkChannel) {
          case 'MAIN':
            if (isWeb()) {
              handleNewAppLinkGnbMain(appLink);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;
          case 'MALL':
            if (isWeb()) {
              handleNewAppLinkGnbMall(appLink);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;
          case 'HOMEIDEA':
            if (isWeb()) {
              handleNewAppLinkGnbHomeIdea(appLink);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;
          case 'REMODELING':
            if (isWeb()) {
              handleNewAppLinkGnbRemodeling(appLink);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;
          default:
            if (isWeb()) {
              handleOpenUrl(appLink.applink.linkUrl);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;
        }
      }
      if (appLink?.applink?.appLinkTarget === 'CATEGORY') {
        switch (appLink?.applink?.appLinkChannel) {
          case 'MALL':
            if (isWeb()) {
              handleNewAppLinkCate(appLink);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;

          case 'HOMEIDEA':
            if (isWeb()) {
              handleNewAppLinkCate(appLink);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;

          default:
            if (isWeb()) {
              handleOpenUrl(appLink.applink.linkUrl);
            } else {
              newOpenApp(appLink?.applink);
            }
            break;
        }
      }

      if (
        appLink?.applink?.appLinkTarget === 'BNB' &&
        appLink?.applink?.appLinkChannel
      ) {
        if (isWeb()) {
          handleOpenUrl(appLink.applink.linkUrl);
        } else {
          newOpenApp(appLink?.applink);
        }
      }

      if (appLink?.applink?.appLinkTarget === 'PLAN') {
        handleOpenUrl(
          `${appLink?.applink?.linkUrl}${
            appLink.applink.appLinkAddQuery
              ? '&' + appLink.applink.appLinkAddQuery
              : ''
          }`,
        );
      }

      if (appLink?.goodsInfo) {
        if (isWeb()) {
          handleOpenUrl(
            `${process.env.NEXT_PUBLIC_STORE_MWEB_HOST}/goods/${appLink.goodsInfo.gdsNo}`,
          );
        } else {
          openApp({
            appLinkTarget: 'GOODS',
            appLinkChannel: 'STORE',
            appLinkVal: `${appLink.goodsInfo.gdsNo}`,
            appLinkSubVal: '',
          });
        }
      }
    },
    [
      handleOpenUrl,
      isWeb,
      handleNewAppLinkGnbMain,
      newOpenApp,
      handleNewAppLinkGnbMall,
      handleNewAppLinkGnbHomeIdea,
      handleNewAppLinkGnbRemodeling,
      handleNewAppLinkCate,
      openApp,
    ],
  );

  const handleLogin = useCallback(async () => {
    if (isApp()) {
      handleOpenApp({ target: 'LOGIN', channel: 'REMODELING' });
    } else {
      await loginGateway.exec();
    }
  }, [handleOpenApp, isApp, loginGateway]);

  const handleGoScrap = useCallback(() => {
    if (isApp()) {
      openApp({
        appLinkTarget: 'LINK',
        appLinkChannel: 'STORE',
        appLinkVal: `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/mmypage/wishList.do`,
      });
    } else {
      router.push(
        `${process.env.NEXT_PUBLIC_HANSSEMMALL_MWEB_HOST}/mmypage/wishList.do`,
      );
    }
  }, [isApp, openApp, router]);

  const handleAction = useCallback<NewDisplayHandleAction>(
    ({ message, action, type }) => {
      console.log('[action]', { message, action, type });

      if (action === 'scrap') {
        toast.success('찜 목록에 추가하였습니다.', {
          placement: 'bottom',
          variant: 'button',
          actionButton: (
            <NewTextButton
              size="xs"
              isUnderline
              color="color_chip.white"
              onClick={handleGoScrap}
              width={52}
            >
              찜 목록가기
            </NewTextButton>
          ),
          offset: 87,
          zIndex: TOAST_Z_INDEX,
        });
      } else {
        switch (type) {
          case 'toast':
            toast.success(message, {
              alignment: 'center',
              placement: 'bottom',
              variant: 'basic',
              offset: 87,
              zIndex: TOAST_Z_INDEX,
            });
            break;

          case 'alert':
            handleAlertOpen({
              type: 'alert',
              textOptions: {
                title: message,
              },
            });
            break;

          case 'confirm':
            if (action === 'login') {
              handleConfirmOpen({
                type: 'confirm',
                textOptions: {
                  title: message,
                },
                cancelText: '아니오',
                confirmText: '예',
                handleConfirm: () => {
                  handleLogin();
                },
              });
            } else {
              handleConfirmOpen({
                type: 'confirm',
                textOptions: {
                  title: message,
                },
                cancelText: '아니오',
                confirmText: '예',
              });
            }
            break;

          default:
            break;
        }
      }
    },
    [handleAlertOpen, handleConfirmOpen, handleLogin, toast],
  );

  return {
    handleLink,
    handleNewLink,
    handleAction,
    handleOpenApp,
    handleOneLink,
  };
};

export default useDisplayComponent;
